import type { BandDepths } from "./daily-obs-types";

export const GLACIER_FIRN_SENTINEL_THRESHOLD_M = 15;
const MAX_BAND_INVERSION_M = 1;
const MAX_BAND_SPREAD_M = 5;

export type DepthConfidence =
  | "full"
  | "partial"
  | "inferred"
  | "unavailable";

export type SnowDepthValidationReason =
  | "valid"
  | "missing"
  | "non-finite"
  | "negative"
  | "glacier-firn-sentinel"
  | "uncertified-model"
  | "returned-cell-outside-tolerance"
  | "band-inversion"
  | "provider-seam";

export type SnowDepthValidation = Readonly<{
  valueM: number | null;
  confidence: DepthConfidence;
  reason: SnowDepthValidationReason;
}>;

export type CertifiedSnowDepthContext = Readonly<{
  model: string;
  certifiedModel: string;
  requestedElevationM: number | null;
  returnedElevationM: number | null;
  elevationToleranceM: number;
  requestedLatitude: number;
  requestedLongitude: number;
  returnedLatitude: number;
  returnedLongitude: number;
  coordinateToleranceDeg: number;
}>;

export function validateSnowDepthM(
  value: unknown,
  context?: CertifiedSnowDepthContext,
): SnowDepthValidation {
  if (value === null || value === undefined) {
    return { valueM: null, confidence: "unavailable", reason: "missing" };
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return {
      valueM: null,
      confidence: "unavailable",
      reason: "non-finite",
    };
  }
  if (value < 0) {
    return { valueM: null, confidence: "unavailable", reason: "negative" };
  }
  if (value >= GLACIER_FIRN_SENTINEL_THRESHOLD_M) {
    return {
      valueM: null,
      confidence: "unavailable",
      reason: "glacier-firn-sentinel",
    };
  }
  if (context) {
    if (context.model !== context.certifiedModel) {
      return {
        valueM: null,
        confidence: "unavailable",
        reason: "uncertified-model",
      };
    }
    if (
      context.requestedElevationM !== null &&
      context.returnedElevationM !== null &&
      Math.abs(
        context.requestedElevationM - context.returnedElevationM,
      ) > context.elevationToleranceM
    ) {
      return {
        valueM: null,
        confidence: "unavailable",
        reason: "returned-cell-outside-tolerance",
      };
    }
    if (
      Math.abs(context.requestedLatitude - context.returnedLatitude) >
        context.coordinateToleranceDeg ||
      Math.abs(context.requestedLongitude - context.returnedLongitude) >
        context.coordinateToleranceDeg
    ) {
      return {
        valueM: null,
        confidence: "unavailable",
        reason: "returned-cell-outside-tolerance",
      };
    }
  }
  return { valueM: value, confidence: "full", reason: "valid" };
}

export type ValidatedBandDepths = Readonly<{
  bandDepths: BandDepths;
  confidence: DepthConfidence;
  reason: SnowDepthValidationReason;
}>;

export function validateBandDepths(
  bandDepths: BandDepths,
  elevations: {
    baseElevM: number | null;
    midElevM: number | null;
    summitElevM: number | null;
  },
): ValidatedBandDepths {
  const validated: BandDepths = {
    baseM: validateSnowDepthM(bandDepths.baseM).valueM,
    midM: validateSnowDepthM(bandDepths.midM).valueM,
    summitM: validateSnowDepthM(bandDepths.summitM).valueM,
    nativeM: validateSnowDepthM(bandDepths.nativeM).valueM,
    nativeElevM: bandDepths.nativeElevM,
    fetchedAt: bandDepths.fetchedAt,
  };
  // Validate each contemporaneous profile independently. Daily maxima can
  // combine different hours and must not invalidate otherwise sound hours.
  if (bandDepths.hourly) {
    const hours = bandDepths.hourly;
    validated.hourly = hours.length <= 24 ? hours.flatMap<NonNullable<BandDepths["hourly"]>[number]>(point => {
      const [hour, baseM, midM, summitM, nativeM] = point;
      if (point.length !== 5 || !Number.isInteger(hour) || hour < 0 || hour > 23 ||
        hours.filter(other => other[0] === hour).length !== 1) return [];
      const checked = validateBandDepths({
        baseM, midM, summitM, nativeM,
        nativeElevM: bandDepths.nativeElevM, fetchedAt: bandDepths.fetchedAt,
      }, elevations).bandDepths;
      return [[hour, checked.baseM, checked.midM, checked.summitM, checked.nativeM]];
    }) : [];
  }
  const points = [
    [elevations.baseElevM, validated.baseM],
    [elevations.midElevM, validated.midM],
    [elevations.summitElevM, validated.summitM],
  ].filter(
    (point): point is [number, number] =>
      point[0] !== null && point[1] !== null,
  ).sort((a, b) => a[0] - b[0]);

  for (let index = 1; index < points.length; index++) {
    if (points[index - 1][1] - points[index][1] > MAX_BAND_INVERSION_M) {
      return {
        bandDepths: { ...emptyBandDepths(bandDepths), ...(validated.hourly ? { hourly: validated.hourly } : {}) },
        confidence: "unavailable",
        reason: "band-inversion",
      };
    }
  }
  if (
    points.length > 1 &&
    Math.max(...points.map((point) => point[1])) -
      Math.min(...points.map((point) => point[1])) > MAX_BAND_SPREAD_M
  ) {
    return {
      bandDepths: { ...emptyBandDepths(bandDepths), ...(validated.hourly ? { hourly: validated.hourly } : {}) },
      confidence: "unavailable",
      reason: "provider-seam",
    };
  }

  const validCount = [
    validated.baseM,
    validated.midM,
    validated.summitM,
    validated.nativeM,
  ].filter((value) => value !== null).length;
  return {
    bandDepths: validated,
    confidence: validCount >= 3
      ? "full"
      : validCount > 0
        ? "partial"
        : "unavailable",
    reason: validCount > 0 ? "valid" : "missing",
  };
}

function emptyBandDepths(source: BandDepths): BandDepths {
  return {
    baseM: null,
    midM: null,
    summitM: null,
    nativeM: null,
    nativeElevM: source.nativeElevM,
    fetchedAt: source.fetchedAt,
  };
}
